import React from "react";
import { Button } from "@/components/ui/button";
import { useGame } from "@/contexts/GameContext";
import {
  Play,
  Zap,
  Settings,
  RotateCcw,
  ShoppingCart,
  Info,
} from "lucide-react";
import { motion } from "framer-motion";
import ScoringRulesDialog from "../game/ScoringRulesDialog";
const appVersion = __APP_VERSION__;

export default function StartScreen() {
  const { state, dispatch } = useGame();

  console.log("🏠 StartScreen - Renderowanie ekranu startowego");

  const startNewGame = () => {
    console.log("🎯 Rozpoczynanie nowej gry (resetuje postęp)");
    // START_GAME resetuje drawnIds i inne stany gry, więc jest OK dla nowej gry
    dispatch({ type: "START_GAME" });
    // Ustawienie screen na 'config' dzieje się po `START_GAME` (jeśli jest taka potrzeba)
    // Lub START_GAME może od razu ustawić screen na 'config'
    // Aby było czysto, najpierw resetujemy, a potem idziemy do konfiguracji nowej gry
    dispatch({ type: "SET_SCREEN", payload: "config" });
    dispatch({ type: "SET_CONFIG_STEP", payload: 1 }); // Zacznij konfigurację od początku
  };

  const openSettings = () => {
    console.log("⚙️ Otwieranie ustawień");
    dispatch({ type: "SET_SCREEN", payload: "settings" });
  };

  const resumeGame = () => {
    console.log("🔄 Wznawianie gry");
    // Zakładamy, że wczytany stan ma poprawny `currentPlayerIndex`, `drawnIds` etc.
    dispatch({ type: "SET_SCREEN", payload: "game" });
  };

  const openShop = () => {
    console.log("🛒 Otwieranie sklepu");
    dispatch({ type: "SET_SCREEN", payload: "shop" });
  };

  const startQuickGame = () => {
    console.log("🚀 Rozpoczynanie szybkiej gry (funkcjonalność do dodania)");
    // Tutaj w przyszłości możesz dodać logikę szybkiej gry,
    // np. z domyślnymi ustawieniami i losowymi graczami.
    // dispatch({ type: "START_QUICK_GAME" }); // Przykładowa akcja
  };

  // Warunek wyświetlenia przycisku "Wznów Grę"
  // Gra musi być rozpoczęta, musi być co najmniej jeden gracz
  const canResume =
    state.gameStarted && state.players && state.players.length > 0;

  return (
    <div className="relative flex flex-col items-center justify-end h-full relative gap-12">
      {/* Przycisk Ustawień - absolutnie pozycjonowany */}
      <header className="w-full flex items-center justify-between">
        <Button
          onClick={openSettings}
          variant="ghost"
          size="icon"
          className="rounded-full"
        >
          <Settings className="size-5" />
        </Button>
        <ScoringRulesDialog />
      </header>

      {/* Logo i tytuł - bardziej kompaktowe */}
      <div className="text-center relative z-10">
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl border border-white/20 glass relative overflow-hidden">
          {/* Dodaj efekt świecenia */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
          <Zap className="w-10 h-10 text-white" />
        </div>

        {/* Lepszy typography */}
        <h1 className="text-5xl md:text-6xl font-black bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent mb-2 tracking-tight">
          DareMe
        </h1>
        <p className="text-lg font-semibold text-foreground/80 mb-1">
          Zakręć imprezą
        </p>
        <p className="text-sm text-muted-foreground">Gra dla odważnych</p>
      </div>

      {/* Przycisk - Rozpocznij grę */}
      <div className="flex flex-col gap-4 w-full max-w-xs relative z-10">
        {canResume && (
          <Button
            onClick={resumeGame}
            size="lg"
            variant="success"
            aria-label="Wznów poprzednią grę"
          >
            <RotateCcw className="size-5" /> {/* Ikona dla wznowienia */}
            Wznów Grę
          </Button>
        )}
        <Button
          onClick={startNewGame}
          size="lg"
          aria-label="Rozpocznij nową grę"
        >
          <Play className="size-5 fill-current" />
          {canResume ? "Nowa Gra" : "Rozpocznij Grę"}
        </Button>
        {/* Przycisk - Sklep */}
        <Button
          onClick={openShop}
          size="lg"
          variant="secondary" // Możesz wybrać inny wariant
          className="w-full"
          aria-label="Otwórz sklep"
        >
          <ShoppingCart className="mr-2 size-5" />
          Sklep (Pakiety)
        </Button>
      </div>

      {/* Dodatkowe elementy UI */}
      <div className=" text-center">
        <p className="text-sm text-muted-foreground mb-1">
          Wersja {appVersion}
        </p>
        <p className="text-xs text-muted-foreground">
          Made with ❤️ for party games
        </p>
      </div>
    </div>
  );
}
